import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DefectDataItem } from '../../types/DefectsDataItem';
import { DefectTabs } from '../../types/DefectTabs';
import { DefectInsightFilterBy } from '../../types/DefectInsightsData';
import { defectKey } from '../../utils/DefectIdentity';
import { RegimeMappingsForRisk } from '../../utils/PscRiskGraphHelper';

export interface DefectCode {
    code: string;
    display_name?: string;
    category?: string;
}

export interface DefectState {
    data: DefectDataItem[];
    loading: boolean;
    error: boolean;
    filteredDefects: DefectDataItem[];
    activeTab: DefectTabs;
    showCloseDefectsDialog: boolean;
    showShareDefectsDialog: boolean;
    selectedVessel: string;
    insightsFilterBy: DefectInsightFilterBy;
    /**
     * Separate from insightsFilterBy so that changing "Show" on one tab does
     * not silently re-cut the other.
     */
    pscRiskFilterBy: DefectInsightFilterBy;
    authorityMappings: Record<string, string[]>;
    defectCodes: DefectCode[];
    regimeMappings: RegimeMappingsForRisk;
    /** "{regime}:{code}" -> name, for the defect code chips. */
    regimeDescriptions: Record<string, string>;
    regimeMappingsLoading: boolean;
    /** Distinguishes "not fetched yet" from "fetched and empty". */
    regimeMappingsLoaded: boolean;
    /** Separates a failed fetch from a genuinely empty result. */
    regimeMappingsError: boolean;
}

export const initialState: DefectState = {
    data: [],
    loading: true,
    error: false,
    filteredDefects: [],
    activeTab: DefectTabs.summary,
    showCloseDefectsDialog: false,
    showShareDefectsDialog: false,
    selectedVessel: "",
    insightsFilterBy: DefectInsightFilterBy.VESSELS,
    pscRiskFilterBy: DefectInsightFilterBy.VESSELS,
    authorityMappings: {},
    defectCodes: [],
    regimeMappings: {},
    regimeDescriptions: {},
    regimeMappingsLoading: false,
    regimeMappingsLoaded: false,
    regimeMappingsError: false
};

export const defectSlice = createSlice({
    name: 'defect',
    initialState,
    reducers: {
        setData: (state, action: PayloadAction<DefectDataItem[]>) => {
            state.data = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setFilteredDefects: (state, action: PayloadAction<DefectDataItem[]>) => {
            state.filteredDefects = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<DefectTabs>) => {
            state.activeTab = action.payload;
        },
        setShowCloseDefectsDialog: (state, action: PayloadAction<boolean>) => {
            state.showCloseDefectsDialog = action.payload;
        },
        setShowShareDefectsDialog: (state, action: PayloadAction<boolean>) => {
            state.showShareDefectsDialog = action.payload;
        },
        setSelectedVessel: (state, action: PayloadAction<string>) => {
            state.selectedVessel = action.payload;
        },
        setInsightsFilterBy: (state, action: PayloadAction<DefectInsightFilterBy>) => {
            state.insightsFilterBy = action.payload;
        },
        setPscRiskFilterBy: (state, action: PayloadAction<DefectInsightFilterBy>) => {
            state.pscRiskFilterBy = action.payload;
        },
        updateData: (state, action: PayloadAction<DefectDataItem[]>) => {
            const updates = new Map(
                action.payload.map((defect) => [defectKey(defect), defect])
            );

            const applyUpdate = (defect: DefectDataItem) =>
                updates.get(defectKey(defect)) ?? defect;

            state.data = state.data.map(applyUpdate);
            state.filteredDefects = state.filteredDefects.map(applyUpdate);
        },
        setRegimeMappings: (state, action: PayloadAction<RegimeMappingsForRisk>) => {
            state.regimeMappings = action.payload;
        },
        setRegimeDescriptions: (
            state,
            action: PayloadAction<Record<string, string>>
        ) => {
            state.regimeDescriptions = action.payload;
        },
        setRegimeMappingsLoading: (state, action: PayloadAction<boolean>) => {
            state.regimeMappingsLoading = action.payload;
        },
        setRegimeMappingsLoaded: (state, action: PayloadAction<boolean>) => {
            state.regimeMappingsLoaded = action.payload;
        },
        setRegimeMappingsError: (state, action: PayloadAction<boolean>) => {
            state.regimeMappingsError = action.payload;
        },
        setAuthorityMappings: (state, action: PayloadAction<Record<string, string[]>>) => {
            state.authorityMappings = action.payload;
        },
        setDefectCodes: (state, action: PayloadAction<DefectCode[]>) => {
            state.defectCodes = action.payload;
        }
    }
});

export const {
    setData,
    setLoading,
    setError,
    setFilteredDefects,
    setActiveTab,
    setShowCloseDefectsDialog,
    setShowShareDefectsDialog,
    setSelectedVessel,
    setInsightsFilterBy,
    setPscRiskFilterBy,
    updateData,
    setAuthorityMappings,
    setRegimeMappings,
    setRegimeDescriptions,
    setRegimeMappingsLoading,
    setRegimeMappingsLoaded,
    setRegimeMappingsError,
    setDefectCodes
} = defectSlice.actions;

export default defectSlice.reducer;

